import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { FormGrid, Field, TextInput, DateInput, SelectInput, TextArea } from '../../components/ui';

// 质检单录入：只记数据，不判合格（超允收客户仍可能接受）。
// 全量参数来自 QC 录入模板（工艺测量）；出西语单时再按 ERPNext 出单模板抽子集。

type TplStage = {
  stage_key: string;
  stage_name: string;
  parameters_json?: string | null;
};

type QcTemplate = {
  id: string;
  code: string;
  name_zh: string;
  source_system?: string;
  stages?: TplStage[];
};

type Row = {
  stage_key: string;
  stage_name: string;
  key: string;
  label_es?: string;
  unit?: string;
  accept?: string;
  r1: string;
  r2: string;
  r3: string;
};

function parseParams(stage: TplStage): string[] {
  try {
    const j = stage.parameters_json ? JSON.parse(stage.parameters_json) : [];
    return Array.isArray(j) ? j.map(String) : [];
  } catch {
    return [];
  }
}

function fmtAccept(row: Record<string, unknown>): string {
  const min = row.min_value as number | null | undefined;
  const max = row.max_value as number | null | undefined;
  const unit = (row.unit as string) || '';
  const u = unit ? ` ${unit}` : '';
  if (min != null && max != null) return `${min} – ${max}${u}`;
  if (min != null) return `≥ ${min}${u}`;
  if (max != null) return `≤ ${max}${u}`;
  const cmp = String(row.comparator || '');
  const tgt = row.target_value as number | null | undefined;
  if (cmp === '>=' && tgt != null) return `> ${tgt}${u}`;
  if (cmp === '<=' && tgt != null) return `< ${tgt}${u}`;
  const tol = row.tolerance as string | number | null | undefined;
  if (tol != null && tol !== '') return `±${tol}${u}`;
  return '—';
}

export function InspectionEditor({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [templates, setTemplates] = useState<QcTemplate[]>([]);
  const [tplId, setTplId] = useState('');
  const [tplName, setTplName] = useState('');
  const [contractId, setContractId] = useState('');
  const [workOrderId, setWorkOrderId] = useState('');
  const [deliveryId, setDeliveryId] = useState('');
  const [itemId, setItemId] = useState('');
  const [serial, setSerial] = useState('');
  const [inspector, setInspector] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState('');
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    let alive = true;
    api.list<QcTemplate>('/api/qc-templates', { limit: 80 }).then((list) => {
      if (!alive) return;
      // 办公室录入也只使用与手机 QC 相同的 10 套全量录入模板。
      // ERPNext 模板是西语出单子集，不应出现在“录入模板”选择器中。
      const sorted = list.filter((t) => t.source_system === 'qi-inspect')
        .sort((a, b) => String(a.code).localeCompare(String(b.code)));
      setTemplates(sorted);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const loadTemplate = async (id: string) => {
    setTplId(id);
    setRows([]);
    setTplName('');
    if (!id) return;
    try {
      const d = await api.detail<QcTemplate & {
        stages?: Array<TplStage & {
          checkpoints?: Array<{ criteria?: Array<Record<string, unknown>> }>;
        }>;
      }>(`/api/qc-templates/${encodeURIComponent(id)}`);
      setTplName(d.name_zh || d.code);
      const critByKey = new Map<string, string>();
      for (const s of d.stages || []) {
        for (const cp of s.checkpoints || []) {
          for (const c of cp.criteria || []) {
            const k = String(c.parameter_key || c.key || '');
            if (!k) continue;
            const t = fmtAccept(c);
            if (t && t !== '—') critByKey.set(k, t);
          }
        }
      }
      const stages = d.stages || [];
      const next: Row[] = [];
      for (const s of stages) {
        for (const p of parseParams(s)) {
          next.push({
            stage_key: s.stage_key, stage_name: s.stage_name, key: p,
            accept: critByKey.get(p) || '—',
            r1: '', r2: '', r3: '',
          });
        }
      }
      if (!next.length) {
        next.push({ stage_key: 'data', stage_name: '检验数据', key: 'value', r1: '', r2: '', r3: '' });
      }
      setRows(next);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '加载模板失败');
    }
  };

  const setCell = (i: number, k: 'r1' | 'r2' | 'r3', v: string) => {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  };

  const submit = async () => {
    setBusy(true); setErr(null); setOk(null);
    try {
      const byStage = new Map<string, { stage_name: string; readings: Record<string, string> }>();
      for (const r of rows) {
        const cur = byStage.get(r.stage_key) || { stage_name: r.stage_name, readings: {} };
        const vals = [r.r1, r.r2, r.r3].map((x) => String(x || '').trim()).filter(Boolean);
        if (!vals.length) continue;
        if (vals.length === 1) cur.readings[r.key] = vals[0];
        else vals.forEach((v, i) => { cur.readings[`${r.key}_${i + 1}`] = v; });
        byStage.set(r.stage_key, cur);
      }
      const stages = [...byStage.entries()].map(([key, s], i) => ({
        stage_key: key,
        stage_name: s.stage_name,
        stage_order: i,
        readings: s.readings,
        result: 'pending',
      }));
      const body: Record<string, unknown> = {
        inspection_date: date,
        inspector_name: inspector || '办公室',
        result: 'pending',
        contract_id: contractId || undefined,
        work_order_id: workOrderId || undefined,
        delivery_id: deliveryId || undefined,
        item_id: itemId || undefined,
        serial_number: serial || undefined,
        qc_template_id: tplId || undefined,
        process_type: tplName || undefined,
        remarks_zh: remarks || undefined,
        stages,
      };
      if (!contractId && !workOrderId && !itemId) {
        setErr('请至少填写：关联合同 / 关联工单 / 物料（质检单须能追溯物料与订单）');
        setBusy(false);
        return;
      }
      const r = await api.post<{ data?: { number?: string } }>('/api/inspections', body);
      const num = r?.data?.number || '';
      setOk(`质检单已记录 ${num}（仅数据，不判合格）`);
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '保存失败');
    } finally { setBusy(false); }
  };

  return (
    <div className="panel" style={{ boxShadow: 'none', padding: '12px var(--pad)', marginBottom: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>新建质检单（只记数据 · 不判合格）</div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
        按 QC 录入模板记录加工过程全部测量；原始/目标尺寸录 1 个值，其余数值项目录 3 个读数。
        后台正式质检表和西语单只显示关联的 ERPNext/Portal 模板项目；QC 全量过程数据不混入正式表格。
      </div>
      {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
      {ok ? <div className="notice ok" style={{ marginBottom: 8 }}>{ok}</div> : null}
      <FormGrid>
        <Field label="检验日期"><DateInput value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="检验人"><TextInput value={inspector} onChange={(e) => setInspector(e.target.value)} placeholder="姓名" /></Field>
        <Field label="关联合同"><TextInput value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="CON-… 或 ID" /></Field>
        <Field label="关联工单"><TextInput value={workOrderId} onChange={(e) => setWorkOrderId(e.target.value)} placeholder="WO-… 或 ID" /></Field>
        <Field label="关联交货单（可后补）"><TextInput value={deliveryId} onChange={(e) => setDeliveryId(e.target.value)} placeholder="REM-…" /></Field>
        <Field label="物料 ID / 编码"><TextInput value={itemId} onChange={(e) => setItemId(e.target.value)} placeholder="物料 UUID 或编码" /></Field>
        <Field label="序列号"><TextInput value={serial} onChange={(e) => setSerial(e.target.value)} /></Field>
        <Field label="录入模板（QC 全量参数）" span>
          <SelectInput value={tplId} onChange={(e) => void loadTemplate(e.target.value)}>
            <option value="">— 选择录入模板 —</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.source_system === 'qi-inspect' ? '[QC] ' : '[ERP] '}{t.code} {t.name_zh || ''}
              </option>
            ))}
          </SelectInput>
        </Field>
      </FormGrid>
      {rows.length ? (
        <div style={{ marginTop: 10, overflowX: 'auto' }}>
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            测量值（可只填 1 个，或填 3 个读数）
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--panel-inset)' }}>
                <th style={{ textAlign: 'left', padding: 6 }}>项目</th>
                <th style={{ textAlign: 'left', padding: 6 }}>参数</th>
                <th style={{ padding: 6 }}>允收指数</th>
                <th style={{ padding: 6, width: 90 }}>读数 1</th>
                <th style={{ padding: 6, width: 90 }}>读数 2</th>
                <th style={{ padding: 6, width: 90 }}>读数 3</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.stage_key}-${r.key}-${i}`} style={{ borderTop: '1px solid var(--line-soft)' }}>
                  <td style={{ padding: 6 }}>{r.stage_name}</td>
                  <td style={{ padding: 6 }}>{r.label_es || r.key}</td>
                  <td style={{ padding: 6 }}>{r.accept || '—'}</td>
                  <td style={{ padding: 4 }}><TextInput value={r.r1} onChange={(e) => setCell(i, 'r1', e.target.value)} /></td>
                  <td style={{ padding: 4 }}><TextInput value={r.r2} onChange={(e) => setCell(i, 'r2', e.target.value)} /></td>
                  <td style={{ padding: 4 }}><TextInput value={r.r3} onChange={(e) => setCell(i, 'r3', e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <div style={{ marginTop: 10 }}>
        <Field label="备注"><TextArea value={remarks} onChange={(e) => setRemarks(e.target.value)} /></Field>
      </div>
      <div className="detail-actions" style={{ marginTop: 10 }}>
        <button className="btn primary" disabled={busy || !rows.length} onClick={() => void submit()}>
          {busy ? '保存中…' : '保存检验数据'}
        </button>
      </div>
    </div>
  );
}
